import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';

/** A toast, split the way ngx-toastr wants it: body first, then title. */
export interface ApiErrorMessage {
  title: string;
  detail: string;
}

/**
 * Backend messages that carry no information. The Tracer API answers a 500
 * with the literal body {"error":"internal error"}, so echoing it verbatim
 * produced the infamous "Error / internal error" toast: technically the
 * server's own words, and completely useless to whoever read it.
 */
const USELESS_DETAILS = ['internal error', 'internal server error', 'error'];

function backendDetail(err: unknown): string {
  const raw = (err as HttpErrorResponse | undefined)?.error;

  const detail = typeof raw === 'string' ? raw : typeof raw?.error === 'string' ? raw.error : '';

  const trimmed = detail.trim();

  return USELESS_DETAILS.includes(trimmed.toLowerCase()) ? '' : trimmed;
}

/**
 * Turn a failed API call into something a person can act on.
 *
 * Written after an outage where every authenticated request answered 500 and
 * the UI faithfully relayed "internal error" — so the only signal available to
 * the user was that something, somewhere, had gone wrong. The actual cause was
 * an empty cache in a neighbouring service. Nobody could have guessed that from
 * the toast, and the diagnosis took hours.
 *
 * The rules below therefore aim at one thing: after reading the message, does
 * the reader know whether to retry, to fix something themselves, or to go and
 * get an engineer? Status codes answer that far better than a relayed string,
 * so the status decides the shape and the backend's own words are added only
 * when they actually say something.
 *
 * @param err    the HttpErrorResponse (or any thrown value)
 * @param action what we were doing, phrased to follow "trying to…" —
 *               e.g. "load the sessions list"
 */
export function describeApiError(err: unknown, action: string): ApiErrorMessage {
  const status = (err as HttpErrorResponse | undefined)?.status ?? 0;
  const detail = backendDetail(err);

  // status 0 is the browser refusing to complete the request at all: offline,
  // DNS, TLS, CORS, or the API simply not listening. Never a backend bug, and
  // the one case the user can often fix themselves.
  if (status === 0) {
    return {
      title: 'Cannot reach the Tracer API',
      detail:
        `No response from ${environment.tracerUrl} while trying to ${action}. ` +
        'Check your connection or VPN — or the API may be down.',
    };
  }

  if (status === 401 || status === 403) {
    return {
      title: 'Tracer could not authorise you',
      detail:
        detail ||
        `Your account was not accepted while trying to ${action}. ` +
          'Signing out and back in may help; if it does not, your access needs to be set up.',
    };
  }

  if (status === 404) {
    return {
      title: 'Not found',
      detail: detail || `Nothing matched while trying to ${action}.`,
    };
  }

  if (status >= 500) {
    return {
      title: 'The Tracer API failed',
      detail: detail
        ? `${detail} (HTTP ${status}, while trying to ${action})`
        : `HTTP ${status} while trying to ${action}, with no explanation from the API. ` +
          'This is a problem on the Tracer side rather than anything you did — ' +
          'the cause will be in the Tracer API logs.',
    };
  }

  return {
    title: 'Something went wrong',
    detail: detail || `HTTP ${status} while trying to ${action}.`,
  };
}
